import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  composeValue,
  defaultValue,
  extractTweakManifest,
  parseNumeric,
  type TweakControl,
} from '@/lib/tweaks';
import { useApp } from '@/store/useApp';

export function TweaksPanel() {
  const artifactHtml = useApp((s) => s.artifactHtml);
  const tweakValues = useApp((s) => s.tweakValues);
  const setTweakValue = useApp((s) => s.setTweakValue);
  const prefillComposer = useApp((s) => s.prefillComposer);

  const manifest = useMemo(() => extractTweakManifest(artifactHtml), [artifactHtml]);

  if (!manifest || manifest.controls.length === 0) {
    return (
      <aside className="flex h-full w-72 shrink-0 flex-col gap-3 border-l border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
        <h2 className="text-sm font-semibold">Tweaks</h2>
        <p className="text-xs text-neutral-500">
          {manifest
            ? 'This design declared an empty tweaks block.'
            : 'No tweakable controls in this design.'}
        </p>
        <Button
          size="sm"
          variant="secondary"
          onClick={() =>
            prefillComposer(
              'Add a `cdc-tweaks` block to this design exposing the primary color, accent color, base spacing, and border radius as tweakable controls.',
            )
          }
        >
          Ask Claude to add controls
        </Button>
      </aside>
    );
  }

  return (
    <aside className="flex h-full w-72 shrink-0 flex-col gap-4 overflow-y-auto border-l border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
      <h2 className="text-sm font-semibold">Tweaks</h2>
      {manifest.controls.map((control) => (
        <TweakControlRow
          key={control.name}
          control={control}
          value={tweakValues.find((t) => t.variable === control.name)?.value}
          onChange={(value) => void setTweakValue(control.name, value)}
        />
      ))}
    </aside>
  );
}

interface RowProps {
  control: TweakControl;
  value: string | undefined;
  onChange: (value: string) => void;
}

function TweakControlRow({ control, value, onChange }: RowProps) {
  const current = value ?? defaultValue(control);

  switch (control.type) {
    case 'color':
      return (
        <Field label={control.label} hint={control.name}>
          <ColorRow value={current} onChange={onChange} />
        </Field>
      );
    case 'select':
      return (
        <Field label={control.label} hint={control.name}>
          <select
            value={current}
            onChange={(e) => onChange(e.target.value)}
            className="w-full rounded border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
          >
            {control.options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </Field>
      );
    case 'length':
    case 'number': {
      return <NumericRow control={control} value={current} onChange={onChange} />;
    }
  }
}

// CSS hex normalisation. Accepts `#abc`, `#abcd`, `#aabbcc`, `#aabbccdd`. Anything
// else falls back to leaving the user's text untouched so they can keep typing.
function normaliseHex(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const m = /^#?([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6,8})$/.exec(trimmed);
  if (!m) return null;
  const raw = m[1]!.toLowerCase();
  if (raw.length === 3 || raw.length === 4) {
    return '#' + raw.split('').map((c) => c + c).join('');
  }
  return '#' + raw;
}

function isValidHex(input: string): boolean {
  return /^#?([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6,8})$/.test(input.trim());
}

/**
 * Designer-friendly color row: a 32×32 swatch (click to open the native picker)
 * + a 7-character hex input you can type into directly. The native picker is
 * still there for fine adjustment, but the hex input is what most people reach
 * for once they know the brand value.
 */
function ColorRow({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [draft, setDraft] = useState(value);

  // External changes (carry-forward, undo) overwrite the draft.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  function commit(input: string) {
    const normalised = normaliseHex(input);
    if (normalised && normalised !== value) {
      onChange(normalised);
      setDraft(normalised);
    } else {
      setDraft(value);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <label
        className="relative h-8 w-8 shrink-0 cursor-pointer rounded border border-neutral-300 shadow-inner dark:border-neutral-700"
        style={{ backgroundColor: isValidHex(draft) ? draft : value }}
        title="Click to open the system color picker"
      >
        <input
          type="color"
          value={isValidHex(draft) ? draft : value}
          onChange={(e) => {
            setDraft(e.target.value);
            onChange(e.target.value);
          }}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
      </label>
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commit(draft)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(draft);
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            setDraft(value);
            (e.target as HTMLInputElement).blur();
          }
        }}
        spellCheck={false}
        autoComplete="off"
        placeholder="#1e293b"
        className="w-full rounded border border-neutral-300 bg-white px-2 py-1 font-mono text-xs uppercase tracking-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 dark:border-neutral-700 dark:bg-neutral-900"
        aria-label="Color hex value"
      />
    </div>
  );
}

function NumericRow({
  control,
  value,
  onChange,
}: {
  control: Extract<TweakControl, { type: 'length' | 'number' }>;
  value: string;
  onChange: (value: string) => void;
}) {
  const min = control.min ?? 0;
  const max = control.max ?? 100;
  const step = control.step ?? 1;
  const numeric = parseNumeric(value, parseNumeric(String(control.default), min));
  const [local, setLocal] = useState(numeric);

  useEffect(() => {
    setLocal(numeric);
  }, [numeric]);

  return (
    <Field label={control.label} hint={`${control.name} · ${composeValue(control, local)}`}>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={local}
        onChange={(e) => {
          const next = Number(e.target.value);
          setLocal(next);
          onChange(composeValue(control, next));
        }}
        className="w-full"
      />
    </Field>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5 text-xs">
      <span className="font-medium text-neutral-700 dark:text-neutral-300">{label}</span>
      {children}
      {hint && <span className="text-[10px] text-neutral-400">{hint}</span>}
    </label>
  );
}
